import axios, {
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  Method,
} from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { defaultDomain } from './default-domain';
import { triggerForcedUpdate } from '../services/app-update.js';
import { replaceTerms } from '../language/lang';
import { KeptText } from '../language/kept-text-value';
import { isUnanswered, reportUnansweredRequest } from './unanswered-request';
import { appVersion } from '../utility/app-version';

// Build version baked in at build time (CI stamps VITE_APP_VERSION per deploy); sent
// on every request as X-Client-Version so the server can reject a stale client build.
// "dev-local" for a plain local build, which the server-side gate ignores. Read through
// appVersion(), the one place the About page also reads it.
const APP_VERSION = appVersion();

/**
 * The CSRF header: the login user id, which the server compares with the id inside the cookie's
 * token on every state-changing request (AgwaAuthenticationOptions.CsrfHeaderName). Exported so
 * the one request in the product that is not sent through this client, the unload release in
 * lock-store.ts, spells the name the same way; a second literal there is how the two drift.
 */
export const CSRF_HEADER_NAME = 'X-AGWA-User-Id';

// Returned from the response interceptor on a version mismatch so the failed request
// neither resolves nor rejects (no spurious error surfaces) - the blocking update
// gate is shown and the page reloads onto the new build.
const NEVER_SETTLES: Promise<never> = new Promise<never>(() => undefined);

/**
 * A 426, or any response carrying X-App-Update-Required, marks this client as a stale build
 * (ClientVersionGate.cs). The header is the reliable signal; the status is the fallback.
 */
function isVersionMismatchResponse(response: AxiosResponse): boolean {
  return (
    response.status === 426 ||
    response.headers?.['x-app-update-required'] === 'true'
  );
}

/**
 * The server's `Idempotency-Replayed` marker on a success (D-148 amended), or undefined when the
 * request ran. Any value other than the two the server sends is ignored rather than guessed at.
 */
function readRequestReplay(response: AxiosResponse): RequestReplay | undefined {
  const value: unknown = response.headers?.['idempotency-replayed'];
  return value === 'same' || value === 'changed' ? value : undefined;
}

/**
 * Whether a response is the server's "still working" answer to a keyed request (#398 C6): 409 with
 * `Idempotency-In-Progress: true`. Another copy of the request, under the same key, is still being
 * carried out; nothing was done for this one and there is no stored answer yet. Both are asked of,
 * because a 409 alone is an ordinary conflict.
 */
function isStillWorkingResponse(response: AxiosResponse): boolean {
  return (
    response.status === 409 &&
    response.headers?.['idempotency-in-progress'] === 'true'
  );
}

/** The rejected-promise form of the same test, for a caller whose validateStatus throws. */
function isVersionMismatchError(error: unknown): boolean {
  if (!axios.isAxiosError(error) || !error.response) {
    return false;
  }
  return isVersionMismatchResponse(error.response);
}

export interface UserProfilePicture {
  pictureBase64: string;
}

/**
 * How a keyed request was answered from the server's record of one already carried out (D-148,
 * amended 2026-09-30), read from the `Idempotency-Replayed` response header. `same`: the request
 * again. `changed`: the same kind of request with different values, edited after its answer was
 * lost; the server answered with what it first made and did not apply the edit.
 */
export type RequestReplay = 'same' | 'changed';

export type ApiResponse<T> =
  | {
      success: true;
      data: T;
      error?: never;
      errorKept?: never;
      status: number;
      /**
       * Set only when a keyed request was answered from its key's record; absent when it ran, so
       * every existing caller is unaffected. A page that sent a key treats `changed` as a success
       * and tells the person their change was not saved (connection-health rule 10).
       */
      replayed?: RequestReplay;
    }
  | {
      success: false;
      data?: never;
      error: string;
      /**
       * The same message as `error`, as the server sent it, tags unresolved: what a toast shows
       * through renderKeptText, so a person tag reads as that person's current name
       * (@VOC-SERVER-002). `error` stays resolved words for every other reader. Never resolve
       * `error` a second time instead: a literal the server decoded, or anything typed, would be
       * read again for tags. Set by every failure built from a response; absent on the ones the
       * client composes itself, which carry no tags.
       */
      errorKept?: KeptText;
      status: number;
      errorData?: unknown;
      problem?: ProblemDetails;
      /**
       * Set when the server was still carrying out another copy of this keyed request each time
       * the client asked, until it gave up asking (#398 C6). The request may yet be carried out by
       * that copy, so a page treats it as unsettled: it shows the failure as any other, but keeps
       * the key (RequestIntentKey.settle), so the next press is the same request and is answered
       * with what that copy made, never a second create. Absent on every other failure.
       */
      inProgress?: true;
      /**
       * Set when the server never answered: refused at the network or timed out, after the
       * client's own retries, and not cancelled on purpose (D-144). A page presents nothing for
       * it and keeps its state; ask with `isUnanswered` (unanswered-request.ts). Absent on every
       * failure the server answered, so existing callers are unaffected.
       */
      unanswered?: true;
    };

// RFC 9457 Problem Details (+ extensions we plan to emit)
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string; // extension
  traceId?: string; // extension
  errors?: Record<string, string[]>; // validation details
  [key: string]: unknown; // forward compatibility
}

export interface RequestOptions {
  correlationId?: string;
  idempotencyKey?: string;
  ifMatch?: string; // ETag concurrency control
  timeoutMs?: number;
  signal?: AbortSignal;
  headers?: Record<string, string | undefined>;
  accept?: string; // override Accept header (e.g., vendor media type)
  retry?: Partial<RetryOptions> | false; // override default retry or disable with false
  autoIdempotencyKey?: boolean; // override global auto-generation (default true)
  /**
   * Whether an unanswered failure is reported to the connection monitor (default true). Only the
   * monitor's own sign-in check turns it off: that request IS the check, and reporting it would
   * ask the monitor to check because its check went unanswered. The failure is still marked.
   */
  reportUnanswered?: boolean;
}

export interface SearchPageEnvelope {
  cursor?: string;
  next?: string;
  hasMore: boolean;
  count?: number;
}

export interface SearchResponseEnvelope<T> {
  data: T[];
  page: SearchPageEnvelope;
  meta?: Record<string, unknown>;
  totalApprox?: number; // may be surfaced by server directly
}

export interface RetryOptions {
  maxAttempts: number; // total attempts including the first
  initialDelayMs: number; // base delay before first retry
  maxDelayMs: number; // cap for exponential backoff
  backoffFactor: number; // multiplier per attempt
  jitterRatio: number; // 0..1 portion of delay used for +/- jitter
  retryableStatusCodes: number[]; // HTTP status codes to retry
  retryNetworkErrors: boolean; // retry when status=0 (network)
  respectRetryAfter: boolean; // parse Retry-After header
  allowRetryForUnsafeWithoutIdempotencyKey: boolean; // safety guard for POST/DELETE/PUT/PATCH
}

/**
 * How long a keyed request keeps asking while the server answers "still working" (#398 C6): it asks
 * again with the same key after `initialDelayMs`, the pause growing by `backoffFactor` up to
 * `maxDelayMs`, and gives up once the next pause would take it past `maxTotalMs` from its first ask.
 */
export interface StillWorkingReAskOptions {
  initialDelayMs: number;
  maxDelayMs: number;
  backoffFactor: number;
  maxTotalMs: number;
}

export interface CircuitBreakerOptions {
  enabled: boolean;
  failureThreshold: number; // consecutive failures before opening
  openStateDurationMs: number; // time to stay open before half-open
  halfOpenTrialRequests: number; // number of trial requests allowed while half-open
  minimumThroughput: number; // optional: minimum total requests before breaker activates
  trackByMethod: boolean; // if true maintain state per HTTP method
}

interface CircuitState {
  consecutiveFailures: number;
  openedAt?: number;
  state: 'closed' | 'open' | 'half-open';
  halfOpenTrials: number;
  /**
   * Whether the failure that last opened the breaker got no answer (D-144). Answered refusals
   * (a 400, a 409) count towards opening it too, and a breaker they opened is no verdict that the
   * server is not answering, so its fail-fast stays the page's to present, as before U21.
   */
  openedByNoAnswer?: boolean;
}

export class AGWASuiteApiClientBase {
  protected readonly axiosInstance: AxiosInstance;
  protected _authToken?: string;
  protected _userId?: string;
  /** Selects which per-ACCOUNT auth cookie the server reads. Shared across tabs by design. */
  protected _accountKey?: string;
  /** Identifies THIS TAB for lock ownership. Must differ between two tabs of one person. */
  protected _tabSessionId?: string;
  public get authToken(): string | undefined {
    return this._authToken;
  }
  public get userId(): string | undefined {
    return this._userId;
  }
  public get accountKey(): string | undefined {
    return this._accountKey;
  }
  public get tabSessionId(): string | undefined {
    return this._tabSessionId;
  }
  protected readonly defaultTimeout: number = 30000;
  protected defaultRetryOptions: RetryOptions = {
    maxAttempts: 3,
    initialDelayMs: 300,
    maxDelayMs: 5000,
    backoffFactor: 2,
    jitterRatio: 0.25,
    retryableStatusCodes: [408, 425, 429, 500, 502, 503, 504],
    retryNetworkErrors: true,
    respectRetryAfter: true,
    allowRetryForUnsafeWithoutIdempotencyKey: false,
  };
  /**
   * The re-ask bound for a "still working" answer (#398 C6). A keyed create normally finishes in
   * well under a second, so the first re-ask comes quickly; the total stays under the request
   * timeout, so a person waiting on a create is answered within the time an ordinary request
   * could take.
   */
  protected stillWorkingReAsk: StillWorkingReAskOptions = {
    initialDelayMs: 250,
    maxDelayMs: 2000,
    backoffFactor: 2,
    maxTotalMs: 15000,
  };
  protected circuitBreakerOptions: CircuitBreakerOptions = {
    enabled: true,
    failureThreshold: 5,
    openStateDurationMs: 15000,
    halfOpenTrialRequests: 2,
    minimumThroughput: 10,
    trackByMethod: false,
  };
  protected circuitState: Record<string, CircuitState> = {};
  protected totalRequests = 0;
  protected autoGenerateIdempotencyKeyForUnsafeRetries = true;

  constructor(baseUrl?: string) {
    const resolvedBaseUrl = baseUrl ?? defaultDomain ?? window.location.origin;
    this.axiosInstance = axios.create({
      baseURL: resolvedBaseUrl,
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Version': APP_VERSION,
      },
      timeout: this.defaultTimeout,
      withCredentials: true, // Enable sending cookies with cross-origin requests
    });

    // Server version gate: a 426 with X-App-Update-Required means this client is a
    // stale build. Show the blocking update gate and stall the request (the page is
    // reloading onto the current build) rather than surfacing a spurious error.
    //
    // The check runs on the FULFILLED branch because `request()` sends every call with
    // `validateStatus: () => true`, so axios resolves a 426 rather than rejecting it. The
    // original interceptor tested only the rejected branch, which no request in the product
    // ever reached: a stale client's sign-in printed "HTTP 426" and nothing reloaded. The
    // rejected branch stays for a caller that supplies a strict validateStatus of its own.
    this.axiosInstance.interceptors.response.use(
      (response) => {
        if (isVersionMismatchResponse(response)) {
          triggerForcedUpdate();
          return NEVER_SETTLES;
        }
        return response;
      },
      (error: unknown) => {
        if (isVersionMismatchError(error)) {
          triggerForcedUpdate();
          return NEVER_SETTLES;
        }
        return Promise.reject(
          error instanceof Error ? error : new Error(String(error))
        );
      }
    );
  }

  /**
   * Sets the authentication token, user ID, and session ID for API requests
   * @param token Optional auth token to include in Authorization header
   * @param userId Optional user ID to include in CSRF protection header
   * @param sessionId Optional session ID for per-tab session isolation
   * @returns The client instance for chaining
   */
  public setAuthToken(
    token: string | undefined,
    userId?: string,
    accountKey?: string,
    tabSessionId?: string
  ) {
    this._userId = userId;
    this._accountKey = accountKey;
    this._tabSessionId = tabSessionId;

    // Always set the CSRF user ID header
    this.axiosInstance.defaults.headers.common[CSRF_HEADER_NAME] = userId;

    // TWO HEADERS, TWO JOBS, AND THEY ARE NOT INTERCHANGEABLE.
    //
    // `X-Session-Id` is a CREDENTIAL SELECTOR. The server names one auth cookie per ACCOUNT and
    // uses this value to choose which to read (AgwaAuthenticationHandler:147-166, which FAILS the
    // request outright when no cookie matches). It is the account key, per user, deliberately
    // shared across tabs so a second tab stays signed in.
    //
    // `X-AGWA-Tab-Session` is WHO HOLDS A LOCK. It is per tab and must differ between two tabs of
    // the same person, which is the whole point of session-based lock ownership (#362).
    //
    // #362 briefly put the tab session into `X-Session-Id` and broke authentication for every
    // request in the product: the handler looked for a cookie named after a GUID, found none, and
    // refused with "Session not found". Do not merge them again.
    if (accountKey) {
      this.axiosInstance.defaults.headers.common['X-Session-Id'] = accountKey;
    } else {
      delete this.axiosInstance.defaults.headers.common['X-Session-Id'];
    }

    if (tabSessionId) {
      this.axiosInstance.defaults.headers.common['X-AGWA-Tab-Session'] =
        tabSessionId;
    } else {
      delete this.axiosInstance.defaults.headers.common['X-AGWA-Tab-Session'];
    }

    // Only set the auth token if it's provided and not empty
    if (token && token.trim() !== '') {
      this._authToken = token;
      this.axiosInstance.defaults.headers.common['Authorization'] =
        `Bearer ${token}`;
    } else {
      // Clear the auth token if not provided
      this._authToken = undefined;
      delete this.axiosInstance.defaults.headers.common['Authorization'];
    }

    return this;
  }

  /**
   * Sets the two session-shaped headers for an anonymous client. See setAuthToken for why they
   * are two values: the account key selects the auth cookie, the tab session says who holds a lock.
   */
  public setSessionHeaders(accountKey?: string, tabSessionId?: string) {
    this._accountKey = accountKey;
    this._tabSessionId = tabSessionId;

    if (accountKey) {
      this.axiosInstance.defaults.headers.common['X-Session-Id'] = accountKey;
    } else {
      delete this.axiosInstance.defaults.headers.common['X-Session-Id'];
    }

    if (tabSessionId) {
      this.axiosInstance.defaults.headers.common['X-AGWA-Tab-Session'] =
        tabSessionId;
    } else {
      delete this.axiosInstance.defaults.headers.common['X-AGWA-Tab-Session'];
    }

    return this;
  }

  /**
   * Override default retry strategy globally for this client instance.
   */
  public setDefaultRetryOptions(options: Partial<RetryOptions>): this {
    this.defaultRetryOptions = { ...this.defaultRetryOptions, ...options };
    return this;
  }

  /** Set circuit breaker options */
  public setCircuitBreakerOptions(
    options: Partial<CircuitBreakerOptions>
  ): this {
    this.circuitBreakerOptions = { ...this.circuitBreakerOptions, ...options };
    return this;
  }

  /** Enable/disable auto idempotency-key generation for unsafe methods with retries */
  public setAutoGenerateIdempotencyKey(enabled: boolean): this {
    this.autoGenerateIdempotencyKeyForUnsafeRetries = enabled;
    return this;
  }

  protected clearAuthToken() {
    this._authToken = undefined;
    this._userId = undefined;
    delete this.axiosInstance.defaults.headers.common['Authorization'];
    delete this.axiosInstance.defaults.headers.common[CSRF_HEADER_NAME];
  }

  protected async postExecute<TInput, TResult>(
    path: string,
    input: TInput
  ): Promise<ApiResponse<TResult>> {
    return this.request<TResult>('POST', path, input, {});
  }

  protected async getExecute<TResult>(
    path: string
  ): Promise<ApiResponse<TResult>> {
    return this.request<TResult>('GET', path, undefined, {});
  }

  // New generalized request method implementing hybrid conventions.
  protected async request<TResult>(
    method: Method,
    path: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<ApiResponse<TResult>> {
    const opts = options ?? {};
    const correlationId = opts.correlationId || this.generateCorrelationId();
    const upperMethod = method.toUpperCase();
    const headers: Record<string, string> = {
      'X-Correlation-Id': correlationId,
    };
    const retryOptions = this.resolveRetryOptions(method, opts);

    // Auto idempotency key generation (unsafe + retries >1)
    const unsafe = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(upperMethod);
    if (
      unsafe &&
      !opts.idempotencyKey &&
      retryOptions.maxAttempts > 1 &&
      (opts.autoIdempotencyKey ??
        this.autoGenerateIdempotencyKeyForUnsafeRetries)
    ) {
      opts.idempotencyKey = this.generateIdempotencyKey();
    }
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    if (opts.ifMatch) headers['If-Match'] = opts.ifMatch;
    if (opts.accept) headers['Accept'] = opts.accept;
    if (opts.headers) {
      for (const [k, v] of Object.entries(opts.headers)) {
        if (v !== undefined && v !== null) headers[k] = v;
      }
    }

    const timeout = opts.timeoutMs ?? this.defaultTimeout;

    const config: AxiosRequestConfig = {
      method,
      url: path,
      data,
      headers,
      timeout,
      signal: opts.signal,
      validateStatus: () => true, // we handle status uniformly
    };

    // Circuit breaker pre-check
    const breakerKey = this.circuitBreakerOptions.trackByMethod
      ? upperMethod
      : 'global';
    const state = this.getCircuitState(breakerKey);
    if (this.circuitBreakerOptions.enabled) {
      const decision = this.evaluateCircuitState(state);
      if (decision === 'block') {
        // A breaker opened by a request that got no answer is the client's own verdict that the
        // server is not answering, so a request it refuses is no answer too (D-144, SA answer on
        // U21): marked, reported, and presented by the connection monitor rather than by the page.
        // One opened by answered refusals is not, and its fail-fast is unmarked, as before U21
        // (U21 review F1). The detail's dash is a plain hyphen, per the house rule on dashes.
        return this.settleFailure<TResult>(
          {
            success: false,
            status: 503,
            error: 'Circuit open',
            errorData: { code: 'CircuitOpen' },
            problem: {
              status: 503,
              title: 'Service temporarily unavailable',
              detail: 'Circuit breaker open - failing fast',
              code: 'CircuitOpen',
            },
            ...(state.openedByNoAnswer ? { unanswered: true as const } : {}),
          },
          opts
        );
      }
    }
    let attempt = 0;
    let lastError: ApiResponse<TResult> | undefined;
    while (attempt < retryOptions.maxAttempts) {
      if (attempt > 0) {
        const delay = this.computeDelay(retryOptions, attempt, lastError);
        if (delay > 0) await this.sleep(delay, opts.signal);
      }
      attempt++;
      try {
        const response = await this.requestUntilNotStillWorking<TResult>(
          config,
          opts
        );
        if (response.status >= 200 && response.status < 300) {
          this.recordSuccess(state);
          const replayed = readRequestReplay(response);
          return {
            success: true,
            status: response.status,
            data: response.data,
            ...(replayed ? { replayed } : {}),
          };
        }
        const problem: ProblemDetails | undefined = this.tryParseProblem(
          response.data
        );
        // Extract error message: prioritize problem details, then plain string response, then fallback
        const errorMessage = this.extractErrorMessage(
          response.status,
          response.data,
          problem
        );
        const built: ApiResponse<TResult> = this.buildFailure<TResult>(
          response.status,
          errorMessage,
          response.data,
          problem
        );
        // Still "still working" when the client stopped asking: marked, so the page keeps its key.
        const failure: ApiResponse<TResult> =
          !built.success &&
          opts.idempotencyKey &&
          isStillWorkingResponse(response)
            ? { ...built, inProgress: true as const }
            : built;
        if (
          !this.shouldRetryHttp(response.status, problem, retryOptions, config)
        ) {
          this.recordFailure(state);
          return failure;
        }
        lastError = failure;
        this.recordFailure(state);
        continue; // retry loop
      } catch (err) {
        const error = err as AxiosError;
        const status = error.response?.status || 0; // 0 network or CORS
        const failure: ApiResponse<TResult> = this.markIfUnanswered(
          this.buildFailure<TResult>(
            status,
            error.message ?? 'Network error',
            error.response?.data
          ),
          err,
          opts.signal
        );
        if (!this.shouldRetryNetwork(status, retryOptions)) {
          this.recordFailure(state, isUnanswered(failure));
          return this.settleFailure(failure, opts);
        }
        lastError = failure;
        this.recordFailure(state, isUnanswered(failure));
        continue;
      }
    }
    // Exhausted attempts. The last attempt's failure is the answer: if it went unanswered, the
    // request did, and it is reported once here rather than once per attempt.
    this.recordFailure(state, isUnanswered(lastError));
    return this.settleFailure(
      lastError ?? {
        success: false,
        status: 0,
        error: 'Request failed after retries',
      },
      opts
    );
  }

  /**
   * Sends the request, and while the server answers it "still working" (#398 C6) asks again with
   * the same request - the same key - after a growing pause, until any other answer comes or the
   * bound in `stillWorkingReAsk` is reached. Returns that other answer, or the last "still working"
   * one when the bound is reached, for the caller to treat as a failure like any other 409.
   *
   * Only a request carrying a key is re-asked: the server answers "still working" only to a keyed
   * request, and asking again is safe only because the key makes the server answer the repeat
   * with the first copy's result rather than carry it out twice. The re-asks are not the retry
   * attempts in RetryOptions: they are answers the server gave, not failures to reach it.
   */
  protected async requestUntilNotStillWorking<TResult>(
    config: AxiosRequestConfig,
    opts: RequestOptions
  ): Promise<AxiosResponse<TResult>> {
    const bound = this.stillWorkingReAsk;
    const startedAt = Date.now();
    let delay = bound.initialDelayMs;
    for (;;) {
      const response = await this.axiosInstance.request<TResult>(config);
      if (!opts.idempotencyKey || !isStillWorkingResponse(response)) {
        return response;
      }
      if (Date.now() - startedAt + delay > bound.maxTotalMs) {
        return response;
      }
      await this.sleep(delay, opts.signal);
      delay = Math.min(delay * bound.backoffFactor, bound.maxDelayMs);
    }
  }

  /**
   * Whether a thrown request error means the server never answered (D-144): an axios error with no
   * response (refused at the network, or timed out), and not a cancellation the caller asked for,
   * which is the caller's own doing and not the connection's.
   */
  protected isNoAnswer(err: unknown, signal?: AbortSignal): boolean {
    if (!axios.isAxiosError(err) || err.response) return false;
    return !axios.isCancel(err) && !signal?.aborted;
  }

  /** The failure, marked `unanswered` when the error that produced it was no answer. */
  protected markIfUnanswered<TResult>(
    failure: ApiResponse<TResult>,
    err: unknown,
    signal?: AbortSignal
  ): ApiResponse<TResult> {
    if (failure.success || !this.isNoAnswer(err, signal)) return failure;
    return { ...failure, unanswered: true as const };
  }

  /**
   * The last step of every failure that leaves `request` after trying the network: a marked one
   * is reported to the connection monitor, once, unless the caller is the monitor's own check.
   */
  protected settleFailure<TResult>(
    failure: ApiResponse<TResult>,
    opts: RequestOptions
  ): ApiResponse<TResult> {
    if (
      !failure.success &&
      failure.unanswered &&
      opts.reportUnanswered !== false
    ) {
      reportUnansweredRequest();
    }
    return failure;
  }

  // Convenience public wrappers for new hybrid style
  public get<TResult>(path: string, options?: RequestOptions) {
    return this.request<TResult>('GET', path, undefined, options);
  }
  public post<TIn, TResult>(path: string, body: TIn, options?: RequestOptions) {
    return this.request<TResult>('POST', path, body, options);
  }
  public put<TIn, TResult>(path: string, body: TIn, options?: RequestOptions) {
    return this.request<TResult>('PUT', path, body, options);
  }
  public patch<TIn, TResult>(
    path: string,
    body: TIn,
    options?: RequestOptions
  ) {
    return this.request<TResult>('PATCH', path, body, options);
  }
  public delete<TResult>(path: string, options?: RequestOptions) {
    return this.request<TResult>('DELETE', path, undefined, options);
  }
  // Action endpoints: resourcePath + ':' + actionName
  public action<TIn, TResult>(
    resourcePath: string,
    actionName: string,
    body?: TIn,
    options?: RequestOptions
  ) {
    const path = `${resourcePath}:${actionName}`;
    return this.request<TResult>('POST', path, body, options);
  }
  // Search: POST body DSL -> paged envelope
  public search<TQuery, TItem>(
    path: string,
    query: TQuery,
    options?: RequestOptions
  ) {
    return this.request<SearchResponseEnvelope<TItem>>(
      'POST',
      path,
      query,
      options
    );
  }

  // Helpers
  protected generateCorrelationId(): string {
    return uuidv4();
  }
  protected generateIdempotencyKey(): string {
    // For now just reuse uuid; can switch to ULID for lexicographic ordering later
    return uuidv4();
  }
  protected tryParseProblem(data: unknown): ProblemDetails | undefined {
    if (!data || typeof data !== 'object') return undefined;
    // Heuristic: presence of at least one RFC 9457 field
    if (
      'status' in data ||
      'title' in data ||
      'type' in data ||
      'detail' in data
    ) {
      return data as ProblemDetails;
    }
    return undefined;
  }

  /**
   * Builds every failed response, and is THE ONE PLACE server-composed text is resolved.
   *
   * The server owns no vocabulary: it emits `{{term loginGroup.lower}}` and the client renders the
   * word from its typed `terms` object at display time (D-027,
   * documentation/engineering/terms-and-vocabulary.md). Something has to resolve those tags, and
   * the boundary is the only place that can do it once. Resolving per call site had already failed
   * in exactly the way per-site work always does: three screens called `replaceTerms`, and
   * `staff-list.ts` and `user-edit-tabs.ts` rendered `resp.error` raw, so the same refusal read as
   * a sentence on one screen and as `{{term ...}}` on another. Every response leaves through here,
   * so no screen can be forgotten and no new screen has to remember.
   *
   * `errorData` is deliberately left RAW. It is the payload a caller may parse - field names,
   * validation keys, codes - not text anybody reads, and rewriting it would corrupt data to fix a
   * display concern. For the same reason `problem` is COPIED rather than edited: `tryParseProblem`
   * returns the very object `errorData` points at, so writing resolved words into it would rewrite
   * the raw payload too.
   *
   * `errorKept` carries the same message unresolved, for the one reader that renders tags as more
   * than words: a toast shows a person tag as the person's name with their card (@VOC-SERVER-002).
   * It is resolved there, once, rather than `error` being resolved twice.
   */
  protected buildFailure<TResult>(
    status: number,
    message: string,
    errorData?: unknown,
    problem?: ProblemDetails
  ): ApiResponse<TResult> {
    return {
      success: false,
      status,
      error: replaceTerms(message),
      errorKept: new KeptText(message),
      errorData,
      problem: problem ? this.resolveProblemTerms(problem) : undefined,
    };
  }

  /**
   * Returns a copy of a ProblemDetails with its two human-readable members resolved.
   *
   * Only `title` and `detail` are display text; `type`, `code`, `instance` and the validation
   * `errors` keys are identifiers a caller matches on, and substituting inside them would change
   * what the client is matching against.
   */
  protected resolveProblemTerms(problem: ProblemDetails): ProblemDetails {
    const resolved: ProblemDetails = { ...problem };
    if (typeof problem.title === 'string') {
      resolved.title = replaceTerms(problem.title);
    }
    if (typeof problem.detail === 'string') {
      resolved.detail = replaceTerms(problem.detail);
    }
    return resolved;
  }

  /**
   * Extract a meaningful error message from various response formats.
   * Handles: RFC 9457 ProblemDetails, plain string responses, and fallback to HTTP status.
   *
   * Returns the message as the server wrote it, tags included: resolution belongs to
   * `buildFailure`, which is the single seam every failed response passes through.
   */
  protected extractErrorMessage(
    status: number,
    data: unknown,
    problem?: ProblemDetails
  ): string {
    // RFC 9457 ProblemDetails - prefer detail over title
    if (problem?.detail) {
      return problem.detail;
    }
    if (problem?.title) {
      return problem.title;
    }
    // Plain string response (common for BadRequest/NotFound with simple message)
    if (typeof data === 'string' && data.length > 0 && data.length < 500) {
      return data;
    }
    // A refusal with no sentence of its own (#398 C14 c). A 403 that carries one is read above like
    // any other failure, so the person reads the server's refusal rather than one word for all of
    // them; callers that branch on a refusal read `status`, never this text.
    if (status === 403) {
      return 'forbidden';
    }
    // Fallback to HTTP status
    return `HTTP ${status}`;
  }

  // Circuit breaker helpers
  protected getCircuitState(key: string): CircuitState {
    if (!this.circuitState[key]) {
      this.circuitState[key] = {
        consecutiveFailures: 0,
        state: 'closed',
        halfOpenTrials: 0,
      };
    }
    return this.circuitState[key];
  }
  protected evaluateCircuitState(state: CircuitState): 'allow' | 'block' {
    const opts = this.circuitBreakerOptions;
    if (!opts.enabled) return 'allow';
    this.totalRequests++;
    if (state.state === 'open') {
      const elapsed = Date.now() - (state.openedAt || 0);
      if (elapsed >= opts.openStateDurationMs) {
        state.state = 'half-open';
        state.halfOpenTrials = 0;
      } else {
        return 'block';
      }
    }
    if (state.state === 'half-open') {
      if (state.halfOpenTrials >= opts.halfOpenTrialRequests) {
        // Too many concurrent trials; block until one finishes
        return 'block';
      }
      state.halfOpenTrials++;
    }
    return 'allow';
  }
  protected recordSuccess(state: CircuitState) {
    const opts = this.circuitBreakerOptions;
    if (!opts.enabled) return;
    state.consecutiveFailures = 0;
    if (state.state !== 'closed') {
      // Close on first success in half-open
      state.state = 'closed';
      state.openedAt = undefined;
      state.halfOpenTrials = 0;
    }
  }
  /** `unanswered`: the failure got no answer, which is recorded if it is the one that opens the breaker. */
  protected recordFailure(state: CircuitState, unanswered = false) {
    const opts = this.circuitBreakerOptions;
    if (!opts.enabled) return;
    state.consecutiveFailures++;
    if (state.state === 'half-open') {
      // immediate reopen
      state.state = 'open';
      state.openedAt = Date.now();
      state.halfOpenTrials = 0;
      state.openedByNoAnswer = unanswered;
      return;
    }
    if (
      state.state === 'closed' &&
      this.totalRequests >= opts.minimumThroughput &&
      state.consecutiveFailures >= opts.failureThreshold
    ) {
      state.state = 'open';
      state.openedAt = Date.now();
      state.openedByNoAnswer = unanswered;
    }
  }

  // Retry helpers
  protected resolveRetryOptions(
    method: Method,
    opts: RequestOptions
  ): RetryOptions {
    if (opts.retry === false)
      return { ...this.defaultRetryOptions, maxAttempts: 1 };
    const merged = { ...this.defaultRetryOptions, ...(opts.retry || {}) };
    // Safety: if method is unsafe & no idempotency key, enforce single attempt unless explicitly allowed
    const unsafe = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(
      method.toUpperCase()
    );
    if (
      unsafe &&
      !opts.idempotencyKey &&
      !merged.allowRetryForUnsafeWithoutIdempotencyKey
    ) {
      return { ...merged, maxAttempts: Math.min(merged.maxAttempts, 1) };
    }
    return merged;
  }
  protected computeDelay(
    opts: RetryOptions,
    attempt: number,
    lastError?: ApiResponse<unknown>
  ): number {
    // attempt starts at 1 for first attempt; delay applies when attempt>1 so use (attempt-1)
    const exp = Math.min(
      opts.initialDelayMs * Math.pow(opts.backoffFactor, attempt - 1),
      opts.maxDelayMs
    );
    const jitterPortion = exp * opts.jitterRatio;
    const base = exp - jitterPortion;
    const jitter = Math.random() * jitterPortion * 2; // +/- jitterRatio range
    // Honor Retry-After if present & smaller than computed (simplified) -> included later via lastError
    if (opts.respectRetryAfter && lastError && 'errorData' in lastError) {
      const retryAfterHeader = this.extractRetryAfterSeconds(
        lastError.errorData
      );
      if (retryAfterHeader !== undefined) {
        const retryAfterMs = retryAfterHeader * 1000;
        return Math.max(retryAfterMs, 0);
      }
    }
    return Math.max(base + jitter, 0);
  }
  protected shouldRetryHttp(
    status: number,
    _problem: ProblemDetails | undefined,
    opts: RetryOptions,
    _config: AxiosRequestConfig
  ): boolean {
    // Never retry authorization failures (401/403) or 400-level client errors except those explicitly listed.
    if (status === 401 || status === 403) return false;
    return opts.retryableStatusCodes.includes(status);
  }
  protected shouldRetryNetwork(status: number, opts: RetryOptions): boolean {
    return status === 0 && opts.retryNetworkErrors;
  }
  protected extractRetryAfterSeconds(errorData: unknown): number | undefined {
    // If server returned problem details with extensions containing retryAfterSeconds
    if (
      errorData &&
      typeof errorData === 'object' &&
      'retryAfterSeconds' in errorData
    ) {
      const val = (errorData as Record<string, unknown>)['retryAfterSeconds'];
      if (typeof val === 'number') return val;
    }
    return undefined;
  }
  protected async sleep(ms: number, signal?: AbortSignal): Promise<void> {
    if (ms <= 0) return;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      const onAbort = () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      if (signal) {
        signal.addEventListener('abort', onAbort);
      }
    });
  }
}
